import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Radar, ChevronDown, ChevronRight, ArrowUpRight, MinusCircle, LogOut,
  OctagonAlert, Info, Loader2,
} from 'lucide-react';
import { strategyLabApi } from '../../lib/api';
import type { LabDecision, LabDecisionKind, LabScanSummary } from '../../types/strategyLab';
import { Badge } from '../ui/badge';

interface StrategyDecisionMonitorProps {
  deploymentId: string;
  /** Drives the live pulse + default-open state; polling still runs when closed-then-opened. */
  running: boolean;
}

/** Poll cadence — the engine ticks every ~5s, so 4s keeps the feed fresh without waste. */
const DECISIONS_POLL_MS = 4000;
const FEED_LIMIT = 40;

interface KindStyle { icon: React.ComponentType<{ className?: string }>; color: string; }

const KIND_STYLE: Record<LabDecisionKind, KindStyle> = {
  entry: { icon: ArrowUpRight, color: 'text-emerald-500' },
  entry_rejected: { icon: MinusCircle, color: 'text-muted-foreground' },
  exit: { icon: LogOut, color: 'text-sky-500' },
  halt: { icon: OctagonAlert, color: 'text-rose-500' },
  info: { icon: Info, color: 'text-muted-foreground' },
  scan: { icon: Radar, color: 'text-primary' },
};

/** Compact "3s ago" / "2m ago" / "1h ago" from a UTC ISO timestamp. */
function fmtAgo(iso?: string | null): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  return `${hrs}h ago`;
}

const DecisionRow: React.FC<{ d: LabDecision }> = ({ d }) => {
  const style = KIND_STYLE[d.kind] ?? KIND_STYLE.info;
  const Icon = style.icon;
  return (
    <div className="flex items-start gap-2 py-1.5 px-2 rounded hover:bg-muted/40">
      <Icon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${style.color}`} />
      <div className="min-w-0 flex-1">
        <p className="text-xs leading-snug">
          {d.symbol && <span className="font-semibold mr-1">{d.symbol}</span>}
          <span className="text-muted-foreground">{d.detail ?? d.reason ?? d.kind}</span>
        </p>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        {d.count > 1 && (
          <span className="text-[10px] text-muted-foreground tabular-nums" title={`${d.count} times`}>
            ×{d.count}
          </span>
        )}
        <span className="text-[10px] text-muted-foreground tabular-nums whitespace-nowrap">
          {fmtAgo(d.last_ts)}
        </span>
      </div>
    </div>
  );
};

const StrategyDecisionMonitor: React.FC<StrategyDecisionMonitorProps> = ({
  deploymentId, running,
}) => {
  const [open, setOpen] = useState(running);
  const [decisions, setDecisions] = useState<LabDecision[]>([]);
  const [lastScan, setLastScan] = useState<LabScanSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    // Reset on (re)mount so React.StrictMode's dev mount→unmount→remount cycle
    // doesn't leave the ref stuck false (which would freeze the feed on "Loading…").
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await strategyLabApi.deploymentDecisions(deploymentId);
      if (!mounted.current) return;
      setDecisions(data.decisions || []);
      setLastScan(data.last_scan ?? null);
      setLoadedOnce(true);
    } catch {
      // Telemetry-only; keep the last known feed rather than blanking it.
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [deploymentId]);

  useEffect(() => {
    if (!open) return undefined;
    load();
    const t = setInterval(load, DECISIONS_POLL_MS);
    return () => clearInterval(t);
  }, [open, load]);

  const scanData = lastScan?.data;
  const matches = scanData?.matches ?? [];

  return (
    <div className="rounded-md border border-border">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
      >
        {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        <Radar className="w-3.5 h-3.5 text-primary" />
        <span>Live decisions</span>
        {running && (
          <span className="flex items-center gap-1 text-[10px] text-emerald-500">
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-60" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
            </span>
            live
          </span>
        )}
        <span className="flex-1" />
        {loading && <Loader2 className="w-3 h-3 animate-spin" />}
      </button>

      {open && (
        <div className="border-t border-border">
          {/* Scan heartbeat — how the strategy is recursing through the market */}
          <div className="px-3 py-2 bg-muted/30 border-b border-border">
            {scanData ? (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs text-muted-foreground">
                    Scanned{' '}
                    <span className="font-semibold text-foreground tabular-nums">
                      {scanData.universe_size ?? 0}
                    </span>{' '}
                    symbols ·{' '}
                    <span className="font-semibold text-primary tabular-nums">
                      {scanData.match_count ?? 0}
                    </span>{' '}
                    matched
                  </p>
                  <span className="text-[10px] text-muted-foreground tabular-nums whitespace-nowrap">
                    {fmtAgo(lastScan?.last_ts)}
                  </span>
                </div>
                {matches.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {matches.slice(0, 12).map((s) => (
                      <Badge key={s} variant="outline" className="px-1.5 py-0 text-[10px] font-normal">
                        {s}
                      </Badge>
                    ))}
                    {(() => {
                      // Overflow is vs the TRUE match count (server caps the chip
                      // list at 25), so "+N" doesn't undercount a big match set.
                      const total = scanData.match_count ?? matches.length;
                      const shown = Math.min(12, matches.length);
                      return total > shown ? (
                        <span className="text-[10px] text-muted-foreground">+{total - shown}</span>
                      ) : null;
                    })()}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                {loadedOnce ? 'Waiting for the next market scan…' : 'Loading…'}
              </p>
            )}
          </div>

          {/* Discrete decision feed, newest activity first */}
          <div className="max-h-64 overflow-y-auto p-1">
            {decisions.length === 0 ? (
              <p className="text-xs text-muted-foreground px-2 py-3 text-center">
                No entries, rejections, or exits yet — the strategy is watching the market.
              </p>
            ) : (
              decisions.slice(0, FEED_LIMIT).map((d, i) => (
                <DecisionRow key={`${d.kind}-${d.symbol ?? ''}-${d.reason ?? ''}-${d.first_ts ?? i}`} d={d} />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StrategyDecisionMonitor;
